import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Col,
  Collapse,
  Container,
  Row,
  TabContent,
} from "reactstrap";
import Swal from "sweetalert2";
import groupSVG from "../../assets/images/svg/group.svg";
import { DASHBOARD_USER_ROLES } from "../../constants";
import { useCurrentUser, useSetCurrentUser } from "../../slices/auth/hooks";
import { editDetailsProfile } from "../../slices/auth/profile/thunk";
import { postInviteCode } from "../../slices/employees/thunk";
import { getCommissionsStructures } from "../../slices/national/thunk";
import { authMe } from "../../slices/thunks";
import { canUserEditTheme } from "../../utils/utils";
import AccessLinksCard from "./Profile/AccessLinks";
import CommissionsStructure from "./Profile/CommissionsStructure";
import CustomTheme from "./Profile/CustomTheme";
import ChangeEmail from "./Profile/Details/ChangeEmail";
import ChangePassword from "./Profile/Details/ChangePassword";
import Details from "./Profile/Details/Details";
import Info from "./Profile/Info";
import IntegrationId from "./Profile/IntegrationId";
import AddCommissionStructureModal from "./Profile/modals/AddCommissionStructureModal";
import OnboardingCompleteModal from "./Profile/modals/OnboardingCompleteModal";
import NavProfile from "./Profile/NavProfile";
import PaymentInfoNew from "./Profile/PaymentInfoNew";

const UserProfile = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const currentUser = useCurrentUser();
  const isUserInvited = currentUser.invited;
  const globalTheme = useSelector(state => state.Theme);

  // ?onboarding-completed=true

  const isWhitelabel = globalTheme?.whitelabelActive;
  const onboardingCompleted = new URLSearchParams(location.search).get(
    "onboarding-completed"
  );

  const [modalOnboardingCompleted, setModalOnboardingCompleted] =
    useState(false);


  useEffect(() => {
    if (currentUser?.role === DASHBOARD_USER_ROLES.GA || currentUser?.role === DASHBOARD_USER_ROLES.BROKER) {
      if (onboardingCompleted) {
        // Check if we should hide the welcome message based on theme and user role
        const shouldHideModal =
          (currentUser?.role === DASHBOARD_USER_ROLES.BROKER && globalTheme?.uiCustomization?.hideBrokerWelcomeMessage) ||
          (currentUser?.role === DASHBOARD_USER_ROLES.GA && globalTheme?.uiCustomization?.hideGAWelcomeMessage);

        if (!shouldHideModal) {
          setModalOnboardingCompleted(true);
        }
      }
    }
  }, [onboardingCompleted, currentUser?.role, globalTheme?.uiCustomization]);

  const setCurrentUser = useSetCurrentUser();
  const [openModalInviteCode, setOpenModalInviteCode] = useState(false);
  const [activeTab, setActiveTab] = useState(isUserInvited ? "3" : "1");

  const showCommissionStructure = currentUser.role === DASHBOARD_USER_ROLES.NATIONAL && !currentUser.hideCommissionStructure;

  const canModifyTheme = canUserEditTheme(
    currentUser,
    globalTheme
  );

  const showCustomTheme =
    (currentUser.role === DASHBOARD_USER_ROLES.NATIONAL ||
      currentUser.role === DASHBOARD_USER_ROLES.GA ||
      currentUser.role === DASHBOARD_USER_ROLES.BROKER)
    && canModifyTheme;



  const [openModalAddCommissionStructure, setOpenModalAddCommissionStructure] =
    useState(false);

  const toggleModalAddCommissionStructure = () => {
    setOpenModalAddCommissionStructure(!openModalAddCommissionStructure);
  };

  const [loadingCommissions, setLoadingCommissions] = useState(false);

  const [isEditing, setIsEditing] = useState(false);

  const showPaymentInformation =
    currentUser.role !== DASHBOARD_USER_ROLES.ADMIN &&
    !currentUser?.inheritPaymentInfo;

  const showAccessLinksCard = true;
  // currentUser.role === DASHBOARD_USER_ROLES.NATIONAL ||
  // currentUser.role === DASHBOARD_USER_ROLES.GA ||
  // currentUser.role === DASHBOARD_USER_ROLES.BROKER;

  const handleEditInformation = () => {
    if (isUserInvited) {
      setActiveTab("3");
    } else {
      setActiveTab("1");
    }
    setIsEditing(!isEditing);
  };

  const [errorMessage, setErrorMessage] = useState("");
  const [success, setSuccess] = useState(false);

  const [loadingUpdate, setLoadingUpdate] = useState(false);


  useEffect(() => {
    const fetchUser = async () => {
      const response = await dispatch(authMe());
      if (response?.payload) {
        setCurrentUser(response.payload);
      }
    };

    fetchUser();
  }, []);

  const tabChange = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const handleSubmitDetails = async (userDetails) => {
    setLoadingUpdate(true);
    try {
      const response = await dispatch(editDetailsProfile(userDetails));

      const { error } = response;

      if (error || !response) {
        Swal.fire({
          title: "Error",
          text: `There was a problem. Please try again. ${response.error || ""
            } `,
          icon: "error",
          confirmButtonText: "Ok",
        });
      } else {
        Swal.fire({
          title: "Success!",
          text: "Profile updated successfully!",
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
        }).then(() => {
          setCurrentUser({ ...currentUser, ...userDetails });
          setIsEditing(false);
        });
      }
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setLoadingUpdate(false);
    }
  };

  // Invite code
  const handlePostInviteCode = async (inviteCode) => {
    try {
      const response = await dispatch(postInviteCode({ inviteCode }));

      if (response?.payload) {
        Swal.fire({
          title: "Success!",
          text: "Invite code used successfully!",
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
        }).then(() => {
          setOpenModalInviteCode(false);
          setCurrentUser({ ...currentUser, ...response.payload });
        });
      } else {
        Swal.fire({
          title: "Error",
          text: "There was a problem. Make sure the invite code is correct and try again.",
          icon: "error",
          confirmButtonText: "Ok",
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const [commissionsStructure, setCommissionsStructure] = useState({
    default: null,
    custom: [],
  });

  const fetchCommissionsStructures = async () => {
    try {
      setLoadingCommissions(true);
      const response = await dispatch(
        getCommissionsStructures({ id: currentUser.id })
      );
      setCommissionsStructure(response.payload);
      setLoadingCommissions(false);
    } catch (error) {
      console.log(error);
      setLoadingCommissions(false);
    }
  };

  useEffect(() => {
    fetchCommissionsStructures();
  }, []);

  const renderEditProfileForm = () => {
    return (
      <div className="mt-4">
        <NavProfile
          activeTab={activeTab}
          tabChange={tabChange}
          currentUser={currentUser}
          isUserInvited={isUserInvited}
          onInviteCode={handlePostInviteCode}
          openModalInviteCode={openModalInviteCode}
          setOpenModalInviteCode={setOpenModalInviteCode}
        />
        <CardBody className="border rounded-4 p-3 ">
          <TabContent activeTab={activeTab}>
            <Details
              isUserInvited={isUserInvited}
              setIsEditing={setIsEditing}
              currentUser={currentUser}
              onUpdateDetails={handleSubmitDetails}
              loading={loadingUpdate}
              successMessage={success}
              errorMessage={errorMessage}
            />
            <ChangeEmail currentUser={currentUser} />
            <ChangePassword currentUser={currentUser} />
          </TabContent>
        </CardBody>
      </div>
    );
  };


  return (
    <React.Fragment>
      <div className="page-content">
        {!isWhitelabel && (
          <OnboardingCompleteModal
            open={modalOnboardingCompleted}
            setOpen={setModalOnboardingCompleted}
          />
        )}

        <Container fluid>
          <div className="mb-3 card shadow-none p-3 mb-5">
            <div className="d-flex align-items-center justify-content-between">
              <div className="d-flex flex-column">
                <h3 className="fw-bold text-dark d-flex align-items-center mb-2">
                  <img src={groupSVG} alt="group" className="me-3" />
                  Welcome!
                </h3>
                <h5 className="text-muted fw-normal mb-0">
                  Here are your account details
                </h5>
              </div>
            </div>
          </div>
          <Row>
            <Col xxl={12}>
              <Card className="p-3 shadow-none">
                <h4>Details</h4>
                <hr />
                <CardBody className="">
                  <Info
                    isEditing={isEditing}
                    onEdit={handleEditInformation}
                    currentUser={currentUser}
                    tabChange={tabChange}
                    isUserInvited={isUserInvited}
                  />
                </CardBody>
                {[
                  DASHBOARD_USER_ROLES.NATIONAL,
                  DASHBOARD_USER_ROLES.GA,
                  DASHBOARD_USER_ROLES.BROKER,
                ].includes(currentUser?.role) && (
                  <div className="px-3 pb-2">
                    <IntegrationId />
                  </div>
                )}
                <Collapse isOpen={isEditing}>
                  {renderEditProfileForm()}
                </Collapse>
              </Card>
            </Col>

            {/* // Payment info */}
            {showPaymentInformation && (
              <Col lg={12}>
                <Card className="p-3 mt-4 shadow-none">
                  <h4>Payment Information</h4>
                  <hr />
                  <CardBody>
                    <PaymentInfoNew currentUser={currentUser} />
                  </CardBody>
                </Card>
              </Col>
            )}
            {showCommissionStructure && commissionsStructure && (
              <Col lg={12}>
                <AddCommissionStructureModal
                  isOpen={openModalAddCommissionStructure}
                  setIsOpen={setOpenModalAddCommissionStructure}
                  nationalId={currentUser.id}
                  onRefresh={fetchCommissionsStructures}
                  totalAmountCommission={commissionsStructure.totalAmount}
                />
                <Card className="p-3 mt-4 shadow-none">
                  <div className="d-flex align-itmes-center justify-content-between">
                    <h4>Commission Structure</h4>
                    <div className="d-flex justify-content-end">
                      <Button
                        onClick={toggleModalAddCommissionStructure}
                        className="btn btn-primary "
                      >
                        Add New
                      </Button>
                    </div>
                  </div>
                  <hr />
                  <CardBody>
                    <CommissionsStructure
                      commissionsStructure={commissionsStructure}
                      loading={loadingCommissions}
                      nationalId={currentUser.id}
                      totalAmount={commissionsStructure.totalAmount}
                      onRefresh={fetchCommissionsStructures}
                    />
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>


          {showAccessLinksCard && (
            <AccessLinksCard />

          )}

          {showCustomTheme && (
            <CustomTheme
              canModify={canModifyTheme} />
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default UserProfile;
